import { useState, useEffect, useRef } from "react";

// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS

import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { courseAPI, tpAPI, quizAPI, assignmentAPI, urlResourceAPI, categoryAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShareButton } from "@/components/ShareButton";
import { DuplicateMoveDialog } from "@/components/DuplicateMoveDialog";
import { 
  Plus, 
  BookOpen, 
  Pencil, 
  Trash2,
  FileText,
  ChevronRight,
  ChevronDown,
  Loader2,
  Eye,
  EyeOff,
  Layers,
  FolderOpen,
  Folder,
  FlaskConical,
  ClipboardList,
  FileCheck,
  ExternalLink,
  X,
  Maximize2,
  Minimize2,
  Copy,
  FolderOutput,
  MoreVertical,
  Globe,
  Share2,
  Tag,
  Palette,
  ChevronUp,
  GripVertical,
  ArrowUpDown
} from "lucide-react";
import { toast } from "sonner";
import { formatDate, cn } from "@/lib/utils";

// Script to inject in iframe srcdoc to handle anchor links properly
const iframeNavScript = `<script data-nav-handler="true">
  document.addEventListener('click', function(e) {
    var link = e.target.closest('a');
    if (!link) return;
    var href = link.getAttribute('href');
    if (!href) return;
    
    if (href.startsWith('#')) {
      e.preventDefault();
      e.stopPropagation();
      var targetId = href.slice(1);
      var target = document.getElementById(targetId) || document.querySelector('[name="' + targetId + '"]');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return false;
    }
    
    if (href.startsWith('http://') || href.startsWith('https://')) {
      e.preventDefault();
      window.open(href, '_blank', 'noopener,noreferrer');
      return false;
    }
  }, true);
<\/script>`;

// Script to auto-resize iframe to its content height
const iframeResizeScript = `<script data-resize-handler="true">
  function reportHeight() {
    var h = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
    if (h > 0) {
      window.parent.postMessage({ type: 'iframe-resize', height: h + 30 }, '*');
    }
  }
  window.addEventListener('load', function() { setTimeout(reportHeight, 200); });
  new MutationObserver(reportHeight).observe(document.body, { childList: true, subtree: true, attributes: true });
  setTimeout(reportHeight, 500);
  setTimeout(reportHeight, 1500);
<\/script>`;

// Helper function to clean HTML content - remove pointer-events-none from Google Slides containers
// and inject script to handle anchor links in iframes to avoid breaking the parent app routing
const cleanHtmlContent = (content) => {
  if (!content) return '';
  // Remove pointer-events-none and select-none from Google Slides containers
  let cleaned = content
    .replace(/pointer-events-none\s*/g, '')
    .replace(/select-none\s*/g, '');
    
  try {
    if (cleaned.includes('iframe')) {
      const parser = new DOMParser();
      
      const processIframes = (documentNode) => {
        let isModified = false;
        const iframes = documentNode.querySelectorAll('iframe');
        iframes.forEach(iframe => {
          // Add fullscreen permissions for embedded content
          if (!iframe.hasAttribute('allowfullscreen')) {
            iframe.setAttribute('allowfullscreen', 'true');
            iframe.setAttribute('webkitallowfullscreen', 'true');
            iframe.setAttribute('mozallowfullscreen', 'true');
            isModified = true;
          }
          // Also set the allow attribute for modern browsers
          let allowAttr = iframe.getAttribute('allow') || '';
          if (!allowAttr.includes('fullscreen')) {
            iframe.setAttribute('allow', allowAttr ? `${allowAttr}; fullscreen` : 'fullscreen');
            isModified = true;
          }

          let srcdoc = iframe.getAttribute('srcdoc');
          if (srcdoc) {
            // Process the inner srcdoc HTML recursively
            let innerDoc = parser.parseFromString(srcdoc, 'text/html');
            let innerModified = processIframes(innerDoc);
            
            if (innerModified) {
              srcdoc = innerDoc.body.innerHTML;
              isModified = true;
            }
            
            if (!srcdoc.includes('data-nav-handler')) {
              const scriptsToInject = iframeNavScript + iframeResizeScript;
              if (srcdoc.includes('</body>')) {
                srcdoc = srcdoc.replace('</body>', scriptsToInject + '</body>');
              } else if (srcdoc.includes('</html>')) {
                srcdoc = srcdoc.replace('</html>', scriptsToInject + '</html>');
              } else {
                srcdoc = srcdoc + scriptsToInject;
              }
              isModified = true;
            }
            
            if (isModified) {
              iframe.setAttribute('srcdoc', srcdoc);
            }
          }
        });
        return isModified;
      };

      const doc = parser.parseFromString(cleaned, 'text/html');
      if (processIframes(doc)) {
        cleaned = doc.body.innerHTML;
      }
    }
  } catch (e) {
    console.error('Error cleaning iframe content:', e);
  }
  
  return cleaned;
};
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { HtmlContentWithPdfSlides } from "@/components/editor/PdfSlidesRenderer";

export default function CoursePage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { canManage, isAdmin } = useAuth();
  const [treeData, setTreeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteType, setDeleteType] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [expandedCourses, setExpandedCourses] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [colorPickerCourseId, setColorPickerCourseId] = useState(null);
  
  // Duplicate/Move dialog state
  const [duplicateMoveOpen, setDuplicateMoveOpen] = useState(false);
  const [duplicateMoveMode, setDuplicateMoveMode] = useState('duplicate');
  const [duplicateMoveType, setDuplicateMoveType] = useState(null);
  const [duplicateMoveId, setDuplicateMoveId] = useState(null);
  const [duplicateMoveTitle, setDuplicateMoveTitle] = useState('');
  
  // Course viewer modal state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewingCourse, setViewingCourse] = useState(null);
  const [courseLoading, setCourseLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const viewerRef = useRef(null);
  
  // Linked sites state
  const [linkedSites, setLinkedSites] = useState({});
  
  // Categories state
  const [categories, setCategories] = useState([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: "", description: "", color: "#7c3aed", order: 0 });
  const [savingCategory, setSavingCategory] = useState(false);
  const [activeTab, setActiveTab] = useState(null);

  // Reorder state
  const [reorderMode, setReorderMode] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [dragState, setDragState] = useState({ draggedId: null, dragOverId: null, dragOverPosition: null });

  // Handler for opening duplicate/move dialog
  const openDuplicateMoveDialog = (mode, type, id, title) => {
    setDuplicateMoveMode(mode);
    setDuplicateMoveType(type);
    setDuplicateMoveId(id);
    setDuplicateMoveTitle(title);
    setDuplicateMoveOpen(true);
  };

  useEffect(() => {
    loadTree();
  }, [workspaceId]);

  // Listen for iframe auto-resize messages (course viewer modal)
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'iframe-resize' && event.data.height) {
        const iframes = document.querySelectorAll('.course-content iframe[srcdoc]');
        iframes.forEach(iframe => {
          if (iframe.contentWindow === event.source) {
            iframe.style.height = event.data.height + 'px';
            iframe.style.minHeight = event.data.height + 'px';
          }
        });
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [viewingCourse]);

  // Load linked sites for all courses
  const loadLinkedSites = async (courses) => {
    const token = localStorage.getItem('token');
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const sitesMap = {};
    
    for (const course of courses) {
      try {
        const response = await fetch(`${API_URL}/api/sites/by-course/${course.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.sites && data.sites.length > 0) {
            // For non-manager users, filter out hidden sites
            const visibleSites = canManage 
              ? data.sites 
              : data.sites.filter(s => s.is_visible !== false);
            if (visibleSites.length > 0) {
              sitesMap[course.id] = visibleSites;
            }
          }
        }
      } catch (error) {
        console.error(`Failed to load sites for course ${course.id}:`, error);
      }
    }
    
    setLinkedSites(sitesMap);
  };

  const loadTree = async () => {
    try {
      const response = await courseAPI.getTree(workspaceId);
      setTreeData(response.data);
      // Expand all courses by default
      const expanded = {};
      response.data.courses.forEach(c => {
        expanded[c.id] = true;
      });
      setExpandedCourses(expanded);
      
      // Set categories from tree data
      if (response.data.categories) {
        setCategories(response.data.categories);
      }
      
      // Load linked sites for all courses
      if (response.data.courses && response.data.courses.length > 0) {
        loadLinkedSites(response.data.courses);
      }
    } catch (error) {
      console.error("Failed to load course tree:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId || !deleteType) return;
    setDeleting(true);

    try {
      switch (deleteType) {
        case 'course':
          await courseAPI.delete(workspaceId, deleteId);
          break;
        case 'tp':
          await tpAPI.delete(workspaceId, deleteId);
          break;
        case 'quiz':
          await quizAPI.delete(workspaceId, deleteId);
          break;
        case 'assignment':
          await assignmentAPI.delete(workspaceId, deleteId);
          break;
        case 'url_resource':
          await urlResourceAPI.delete(workspaceId, deleteId);
          break;
      }
      toast.success("Supprimé avec succès");
      loadTree(); // Reload tree
    } catch (error) {
      console.error("Failed to delete:", error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleting(false);
      setDeleteId(null);
      setDeleteType(null);
    }
  };

  const handleToggleVisibility = async (type, id, courseId = null) => {
    try {
      let response;
      switch (type) {
        case 'course':
          response = await courseAPI.toggleVisibility(workspaceId, id);
          break;
        case 'section':
          // id is sectionId, courseId is the parent course
          response = await courseAPI.toggleSectionVisibility(workspaceId, courseId, id);
          break;
        case 'tp':
          response = await tpAPI.toggleVisibility(workspaceId, id);
          break;
        case 'quiz':
          response = await quizAPI.toggleVisibility(workspaceId, id);
          break;
        case 'assignment':
          response = await assignmentAPI.toggleVisibility(workspaceId, id);
          break;
        case 'url_resource':
          response = await urlResourceAPI.toggleVisibility(workspaceId, id);
          break;
      }
      toast.success(response.data.message || "Visibilité modifiée");
      loadTree(); // Reload tree
    } catch (error) {
      console.error("Failed to toggle visibility:", error);
      toast.error("Erreur lors de la modification");
    }
  };

  const toggleCourseExpand = (courseId) => {
    setExpandedCourses(prev => ({
      ...prev,
      [courseId]: !prev[courseId]
    }));
  };

  const toggleSectionExpand = (courseId, sectionId) => {
    const key = `${courseId}-${sectionId}`;
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Open course viewer modal
  const openCourseViewer = async (courseOrId, section = null) => {
    setCourseLoading(true);
    setViewerOpen(true);
    try {
      // If courseOrId is an object with id, it's a course object
      const courseId = typeof courseOrId === 'object' ? courseOrId.id : courseOrId;
      const response = await courseAPI.get(workspaceId, courseId);
      setViewingCourse(response.data);
      
      // If a specific section was requested, open that section
      if (section) {
        const foundSection = response.data.sections?.find(s => s.id === section.id);
        setActiveSection(foundSection || section);
      } else if (response.data.sections?.length > 0) {
        setActiveSection(response.data.sections[0]);
      }
    } catch (error) {
      console.error("Failed to load course:", error);
      toast.error("Erreur lors du chargement du cours");
      setViewerOpen(false);
    } finally {
      setCourseLoading(false);
    }
  };

  // Close course viewer
  const closeCourseViewer = () => {
    if (isFullscreen) {
      document.exitFullscreen?.();
    }
    setViewerOpen(false);
    setViewingCourse(null);
    setActiveSection(null);
    setIsFullscreen(false);
  };

  // Toggle fullscreen
  const toggleFullscreen = async () => {
    try {
      if (!isFullscreen) {
        await viewerRef.current?.requestFullscreen?.();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen?.();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error("Fullscreen error:", error);
    }
  };

  // Listen to fullscreen change
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Render item (TP, Quiz, Assignment, URL Resource)
  const renderItem = (item, type, isHidden, indent = 0) => {
    const icon = type === 'tp' ? FlaskConical : type === 'quiz' ? ClipboardList : type === 'url_resource' ? Globe : FileCheck;
    const IconComponent = icon;
    const bgColor = type === 'tp' ? 'bg-orange-100 text-orange-600' : type === 'quiz' ? 'bg-purple-100 text-purple-600' : type === 'url_resource' ? 'bg-cyan-100 text-cyan-600' : 'bg-blue-100 text-blue-600';
    const typeLabel = type === 'tp' ? 'TP' : type === 'quiz' ? 'Quiz' : type === 'url_resource' ? 'URL' : 'Devoir';
    const editPath = type === 'tp' 
      ? `/workspace/${workspaceId}/tp/${item.id}` 
      : type === 'quiz' 
        ? `/workspace/${workspaceId}/quiz/${item.id}/edit`
        : type === 'url_resource'
          ? `/workspace/${workspaceId}/url-resources`
          : `/workspace/${workspaceId}/assignments`;
    const viewPath = type === 'tp' 
      ? `/workspace/${workspaceId}/tp/${item.id}/view` 
      : type === 'quiz' 
        ? `/workspace/${workspaceId}/quiz/${item.id}/take`
        : type === 'url_resource'
          ? item.url
          : `/workspace/${workspaceId}/assignments`;
    
    const isItemHidden = isHidden || item.is_visible === false;

    return (
      <div 
        key={`${type}-${item.id}`}
        className={cn(
          "flex items-center gap-3 py-2 px-4 hover:bg-muted/50 transition-colors",
          isItemHidden && "opacity-50"
        )}
        style={{ marginLeft: `${indent * 16}px` }}
      >
        {/* Tree line */}
        <div className="flex items-center gap-2 text-muted-foreground">
          <div className="w-4 h-px bg-border" />
          <div className={cn("p-1.5 rounded", bgColor)}>
            <IconComponent className="h-3.5 w-3.5" />
          </div>
        </div>

        {/* Item info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs shrink-0">{typeLabel}</Badge>
            <span className={cn("text-sm font-medium truncate", isItemHidden && "text-muted-foreground")}>
              {item.title}
            </span>
          </div>
        </div>

        {/* Visibility badge */}
        {isHidden ? (
          <Badge variant="outline" className="text-xs text-yellow-600 border-yellow-200 shrink-0">
            Caché (parent)
          </Badge>
        ) : item.is_visible === false ? (
          <Badge variant="outline" className="text-xs text-yellow-600 border-yellow-200 shrink-0">
            Caché
          </Badge>
        ) : (
          <Badge variant="outline" className="text-xs text-green-600 border-green-200 shrink-0">
            Visible
          </Badge>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => {
                    if (type === 'url_resource') {
                      window.open(item.url, item.open_in_new_tab !== false ? '_blank' : '_self', 'noopener,noreferrer');
                    } else {
                      navigate(viewPath);
                    }
                  }}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{type === 'url_resource' ? 'Ouvrir le lien' : 'Voir'}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {canManage && (
            <>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleToggleVisibility(type, item.id)}
                      disabled={isHidden}
                    >
                      {item.is_visible === false ? (
                        <EyeOff className="h-3.5 w-3.5 text-yellow-600" />
                      ) : (
                        <Eye className="h-3.5 w-3.5 text-green-600" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {item.is_visible === false ? "Rendre visible" : "Cacher"}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => navigate(editPath)}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => {
                  setDeleteId(item.id);
                  setDeleteType(type);
                }}
              >
                <Trash2 className="h-3.5 w-3.5 text-destructive" />
              </Button>
              <ShareButton
                contentType={type}
                contentId={item.id}
                workspaceId={workspaceId}
                title={item.title}
                variant="ghost"
                size="icon"
                className="h-7 w-7"
              />
              {/* More actions dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`item-actions-${type}-${item.id}`}>
                    <MoreVertical className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 z-[100]" sideOffset={5}>
                  <DropdownMenuItem onClick={() => openDuplicateMoveDialog('duplicate', type, item.id, item.title)}>
                    <Copy className="h-4 w-4 mr-2" />
                    Dupliquer
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openDuplicateMoveDialog('move', type, item.id, item.title)}>
                    <FolderOutput className="h-4 w-4 mr-2" />
                    Déplacer
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </div>
    );
  };

  // Render section with its items
  const renderSection = (course, section, index, isCourseHidden) => {
    const courseItems = treeData.course_items[course.id];
    const sectionItems = courseItems?.sections[section.id] || { tps: [], quizzes: [], assignments: [], url_resources: [] };
    const hasItems = sectionItems.tps.length + sectionItems.quizzes.length + sectionItems.assignments.length + (sectionItems.url_resources?.length || 0) > 0;
    const isSectionHidden = isCourseHidden || section.is_visible === false;
    const isExpanded = expandedSections[`${course.id}-${section.id}`] !== false;

    return (
      <div key={section.id}>
        {/* Section header */}
        <div 
          className={cn(
            "flex items-center gap-3 py-2 px-4 hover:bg-muted/50 cursor-pointer transition-colors",
            isSectionHidden && "opacity-50"
          )}
          style={{ marginLeft: '32px' }}
        >
          {/* Tree line and expand button */}
          <div 
            className="flex items-center gap-2 text-muted-foreground"
            onClick={() => hasItems && toggleSectionExpand(course.id, section.id)}
          >
            <div className="w-4 h-px bg-border" />
            {hasItems ? (
              isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
            ) : (
              <div className="w-4" />
            )}
          </div>

          {/* Section number badge */}
          <span 
            className={cn(
              "text-xs font-bold px-2 py-1 rounded",
              isSectionHidden ? "bg-yellow-200 text-yellow-800" : "bg-primary/20 text-primary"
            )}
            onClick={() => hasItems && toggleSectionExpand(course.id, section.id)}
          >
            {index + 1}
          </span>
          
          {/* Section title */}
          <span 
            className={cn(
              "flex-1 text-sm font-medium truncate cursor-pointer",
              isSectionHidden && "text-muted-foreground"
            )}
            onClick={() => hasItems && toggleSectionExpand(course.id, section.id)}
          >
            {section.title}
          </span>

          {/* Item count */}
          {hasItems && (
            <span className="text-xs text-muted-foreground">
              {sectionItems.tps.length + sectionItems.quizzes.length + sectionItems.assignments.length + (sectionItems.url_resources?.length || 0)} élément(s)
            </span>
          )}

          {/* Section visibility badge */}
          {isCourseHidden ? (
            <Badge variant="outline" className="text-xs text-yellow-600 border-yellow-200">
              Caché (cours)
            </Badge>
          ) : section.is_visible === false ? (
            <Badge variant="outline" className="text-xs text-yellow-600 border-yellow-200">
              Cachée
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs text-green-600 border-green-200">
              Visible
            </Badge>
          )}

          {/* Section view button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/workspace/${workspaceId}/courses/${course.id}/sections/${section.id}`);
                  }}
                  data-testid={`view-section-${section.id}`}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Voir la section</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Section visibility toggle button */}
          {canManage && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleVisibility('section', section.id, course.id);
                    }}
                    disabled={isCourseHidden}
                    data-testid={`toggle-section-visibility-${section.id}`}
                  >
                    {section.is_visible === false ? (
                      <EyeOff className="h-3.5 w-3.5 text-yellow-600" />
                    ) : (
                      <Eye className="h-3.5 w-3.5 text-green-600" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {section.is_visible === false ? "Rendre visible" : "Cacher la section"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {/* Section items */}
        {hasItems && isExpanded && (
          <div className="bg-muted/20">
            {sectionItems.tps.map(tp => renderItem(tp, 'tp', isSectionHidden, 4))}
            {sectionItems.quizzes.map(quiz => renderItem(quiz, 'quiz', isSectionHidden, 4))}
            {sectionItems.assignments.map(assignment => renderItem(assignment, 'assignment', isSectionHidden, 4))}
            {(sectionItems.url_resources || []).map(ur => renderItem(ur, 'url_resource', isSectionHidden, 4))}
          </div>
        )}
      </div>
    );
  };

  // Group courses by category (if categories exist) or by chapter
  const hasCategories = categories.length > 0;
  
  // ===== REORDER FUNCTIONS =====
  const handleMoveUp = async (course, groupCourses) => {
    const idx = groupCourses.findIndex(c => c.id === course.id);
    if (idx <= 0) return;
    const newOrder = [...groupCourses];
    [newOrder[idx - 1], newOrder[idx]] = [newOrder[idx], newOrder[idx - 1]];
    try {
      setReordering(true);
      await courseAPI.reorder(workspaceId, newOrder.map(c => c.id));
      await loadTree();
      toast.success("Ordre mis à jour");
    } catch (err) {
      toast.error("Erreur lors de la réorganisation");
    } finally {
      setReordering(false);
    }
  };

  const handleMoveDown = async (course, groupCourses) => {
    const idx = groupCourses.findIndex(c => c.id === course.id);
    if (idx < 0 || idx >= groupCourses.length - 1) return;
    const newOrder = [...groupCourses];
    [newOrder[idx], newOrder[idx + 1]] = [newOrder[idx + 1], newOrder[idx]];
    try {
      setReordering(true);
      await courseAPI.reorder(workspaceId, newOrder.map(c => c.id));
      await loadTree();
      toast.success("Ordre mis à jour");
    } catch (err) {
      toast.error("Erreur lors de la réorganisation");
    } finally {
      setReordering(false);
    }
  };

  const handleDragStart = (e, courseId) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", courseId);
    setDragState(prev => ({ ...prev, draggedId: courseId }));
  };

  const handleDragOver = (e, courseId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (courseId === dragState.draggedId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const position = e.clientY < midY ? "before" : "after";
    setDragState(prev => ({ ...prev, dragOverId: courseId, dragOverPosition: position }));
  };

  const handleDragLeave = (e) => {
    // Only reset if we actually left the element
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragState(prev => ({ ...prev, dragOverId: null, dragOverPosition: null }));
    }
  };

  const handleDrop = async (e, groupCourses) => {
    e.preventDefault();
    const draggedId = e.dataTransfer.getData("text/plain");
    const { dragOverId, dragOverPosition } = dragState;
    setDragState({ draggedId: null, dragOverId: null, dragOverPosition: null });

    if (!draggedId || !dragOverId || draggedId === dragOverId) return;

    const newOrder = groupCourses.filter(c => c.id !== draggedId);
    const draggedCourse = groupCourses.find(c => c.id === draggedId);
    if (!draggedCourse) return;

    const targetIdx = newOrder.findIndex(c => c.id === dragOverId);
    const insertIdx = dragOverPosition === "before" ? targetIdx : targetIdx + 1;
    newOrder.splice(insertIdx, 0, draggedCourse);

    try {
      setReordering(true);
      await courseAPI.reorder(workspaceId, newOrder.map(c => c.id));
      await loadTree();
      toast.success("Ordre mis à jour");
    } catch (err) {
      toast.error("Erreur lors de la réorganisation");
    } finally {
      setReordering(false);
    }
  };

  const handleDragEnd = () => {
    setDragState({ draggedId: null, dragOverId: null, dragOverPosition: null });
  };

  const coursesByGroup = treeData ? treeData.courses.reduce((acc, course) => {
    let groupKey;
    if (hasCategories) {
      groupKey = course.category_id || "__uncategorized__";
    } else {
      groupKey = course.chapter || "Sans chapitre";
    }
    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(course);
    return acc;
  }, {}) : {};

  // Sort courses within each group by order
  Object.keys(coursesByGroup).forEach(key => {
    coursesByGroup[key].sort((a, b) => (a.order || 0) - (b.order || 0));
  });

  // Build sorted groups
  const sortedGroups = (() => {
    if (hasCategories) {
      const result = [];
      // Add categories in their order
      categories.forEach(cat => {
        if (coursesByGroup[cat.id]) {
          result.push({ key: cat.id, label: cat.name, color: cat.color, courses: coursesByGroup[cat.id] });
        }
      });
      // Add uncategorized at the end
      if (coursesByGroup["__uncategorized__"]) {
        result.push({ key: "__uncategorized__", label: "Sans catégorie", color: null, courses: coursesByGroup["__uncategorized__"] });
      }
      return result;
    } else {
      return Object.entries(coursesByGroup)
        .sort((a, b) => {
          if (a[0] === "Sans chapitre") return 1;
          if (b[0] === "Sans chapitre") return -1;
          const minOrderA = Math.min(...a[1].map(c => c.order || 0));
          const minOrderB = Math.min(...b[1].map(c => c.order || 0));
          return minOrderA - minOrderB;
        })
        .map(([key, courses]) => ({ key, label: key, color: null, courses }));
    }
  })();

  const hasUnassignedItems = treeData?.unassigned && 
        (treeData.unassigned.tps.length > 0 || treeData.unassigned.quizzes.length > 0 || treeData.unassigned.assignments.length > 0 || (treeData.unassigned.url_resources?.length || 0) > 0);

  useEffect(() => {
    if (!treeData) return;
    
    let currentValid = false;
    if (activeTab === 'unassigned' && hasUnassignedItems) {
      currentValid = true;
    } else if (activeTab && sortedGroups.some(g => g.key === activeTab)) {
      currentValid = true;
    }

    if (!currentValid) {
      if (sortedGroups.length > 0) {
        setActiveTab(sortedGroups[0].key);
      } else if (hasUnassignedItems) {
        setActiveTab('unassigned');
      } else {
        setActiveTab(null);
      }
    }
  }, [treeData, categories, activeTab, sortedGroups, hasUnassignedItems]);

  // Category handlers
  const openCategoryDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({ name: category.name, description: category.description || "", color: category.color || "#7c3aed", order: category.order || 0 });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: "", description: "", color: "#7c3aed", order: categories.length });
    }
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) return;
    setSavingCategory(true);
    try {
      if (editingCategory) {
        await categoryAPI.update(workspaceId, editingCategory.id, categoryForm);
        toast.success("Catégorie modifiée");
      } else {
        await categoryAPI.create(workspaceId, categoryForm);
        toast.success("Catégorie créée");
      }
      setCategoryDialogOpen(false);
      loadTree();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDeleteCategory = async (catId) => {
    try {
      await categoryAPI.delete(workspaceId, catId);
      toast.success("Catégorie supprimée");
      loadTree();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="page-container">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 rounded-lg bg-muted animate-pulse" />
            ))}
          </div>
        </div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Arborescence des Cours</h1>
            <p className="mt-1 text-muted-foreground">
              {treeData?.courses.length || 0} cours, avec TPs, Quiz et Devoirs associés
            </p>
          </div>
          {canManage && (
            <div className="flex gap-2">
              <Button
                variant={reorderMode ? "default" : "outline"}
                className={cn("gap-2", reorderMode && "bg-indigo-600 hover:bg-indigo-700 text-white")}
                onClick={() => setReorderMode(!reorderMode)}
                disabled={reordering}
              >
                <ArrowUpDown className="h-4 w-4" />
                {reorderMode ? "Terminer" : "Réorganiser"}
              </Button>
              <Button variant="outline" className="gap-2" onClick={() => openCategoryDialog()} data-testid="manage-categories-btn">
                <Tag className="h-4 w-4" />
                Catégories
              </Button>
              <Link to={`/workspace/${workspaceId}/courses/new`}>
                <Button className="gap-2" data-testid="create-course-btn">
                  <Plus className="h-4 w-4" />
                  Nouveau cours
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Courses Tree */}
        {(!treeData?.courses || treeData.courses.length === 0) ? (
          <Card className="mt-8 border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <BookOpen className="h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 font-semibold">Aucun cours</h3>
              <p className="mt-2 text-sm text-muted-foreground text-center">
                {canManage 
                  ? "Créez votre premier cours pour commencer"
                  : "Aucun cours disponible pour le moment"
                }
              </p>
              {canManage && (
                <Link to={`/workspace/${workspaceId}/courses/new`}>
                  <Button className="mt-4 gap-2">
                    <Plus className="h-4 w-4" />
                    Créer un cours
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="mt-8">
            {/* Tabs Header */}
            <div className="flex flex-wrap gap-3 mb-6 relative z-10 w-full">
              {sortedGroups.map((group) => (
                <div 
                  key={group.key}
                  onClick={() => setActiveTab(group.key)}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-all select-none min-w-max",
                    activeTab === group.key 
                      ? "bg-primary/5 border-primary shadow-sm ring-1 ring-primary/20" 
                      : "bg-card hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                  )}
                >
                  {group.color ? (
                    <div className="w-3.5 h-3.5 rounded" style={{ backgroundColor: group.color }} />
                  ) : (
                    <FileText className={cn("h-4 w-4", activeTab === group.key ? "text-primary" : "")} />
                  )}
                  <span className={cn("font-medium text-sm", activeTab === group.key ? "text-foreground" : "")}>
                    {group.label}
                  </span>
                  <span className="text-xs opacity-70 ml-1">{group.courses.length}</span>
                  
                  {canManage && hasCategories && group.key !== "__uncategorized__" && activeTab === group.key && (
                    <div className="flex items-center ml-2 border-l pl-2 gap-0.5" onClick={(e) => e.stopPropagation()}>
                      <div 
                        className="p-1.5 hover:bg-background rounded-md text-muted-foreground hover:text-foreground transition-colors"
                        onClick={(e) => { e.stopPropagation(); openCategoryDialog(categories.find(c => c.id === group.key)); }}
                        title="Modifier la catégorie"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </div>
                      <div 
                        className="p-1.5 hover:bg-destructive/10 rounded-md text-muted-foreground hover:text-destructive transition-colors"
                        onClick={(e) => { e.stopPropagation(); handleDeleteCategory(group.key); }}
                        title="Supprimer la catégorie"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {hasUnassignedItems && (
                <div 
                  onClick={() => setActiveTab('unassigned')}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-all select-none min-w-max",
                    activeTab === 'unassigned' 
                      ? "bg-primary/5 border-primary shadow-sm ring-1 ring-primary/20" 
                      : "bg-card hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                  )}
                >
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className={cn("font-medium text-sm", activeTab === 'unassigned' ? "text-foreground" : "")}>
                    Non rattachés à un cours
                  </span>
                  <span className="text-xs opacity-70 ml-1">
                    {treeData.unassigned.tps.length + treeData.unassigned.quizzes.length + treeData.unassigned.assignments.length + (treeData.unassigned.url_resources?.length || 0)}
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {sortedGroups.map((group) => {
                if (group.key !== activeTab) return null;
                
                return (
              <div key={group.key} className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                <div className="space-y-2">
                  {group.courses.map((course) => {
                    const hasSections = course.sections && course.sections.length > 0;
                    const courseItems = treeData.course_items[course.id] || { tps: [], quizzes: [], assignments: [], url_resources: [], sections: {} };
                    const courseDirectItems = courseItems.tps.length + courseItems.quizzes.length + courseItems.assignments.length + (courseItems.url_resources?.length || 0);
                    const hasLinkedSites = linkedSites[course.id] && linkedSites[course.id].length > 0;
                    const hasContent = hasSections || courseDirectItems > 0 || hasLinkedSites;
                    const isExpanded = expandedCourses[course.id];
                    const isCourseHidden = course.is_visible === false;

                    return (
                      <div 
                        key={course.id}
                        draggable={reorderMode && canManage}
                        onDragStart={reorderMode ? (e) => handleDragStart(e, course.id) : undefined}
                        onDragOver={reorderMode ? (e) => handleDragOver(e, course.id) : undefined}
                        onDragLeave={reorderMode ? handleDragLeave : undefined}
                        onDrop={reorderMode ? (e) => handleDrop(e, group.courses) : undefined}
                        onDragEnd={reorderMode ? handleDragEnd : undefined}
                        className={cn(
                          "border rounded-lg transition-all overflow-hidden",
                          isCourseHidden && "border-yellow-300 bg-yellow-50/50",
                          reorderMode && canManage && "cursor-grab active:cursor-grabbing",
                          dragState.draggedId === course.id && "opacity-40 scale-[0.98]",
                          dragState.dragOverId === course.id && dragState.dragOverPosition === "before" && "ring-2 ring-indigo-400 ring-offset-2 border-t-4 border-t-indigo-500",
                          dragState.dragOverId === course.id && dragState.dragOverPosition === "after" && "ring-2 ring-indigo-400 ring-offset-2 border-b-4 border-b-indigo-500"
                        )}
                        style={course.color ? { 
                          borderLeft: `4px solid ${course.color}`,
                          backgroundColor: `${course.color}10`,
                          borderColor: `${course.color}40`
                        } : {}}
                      >
                        {/* Course Header */}
                        <div 
                          className={cn(
                            "flex items-center gap-3 p-4 cursor-pointer hover:bg-muted/30 transition-colors",
                            isCourseHidden && "opacity-70"
                          )}
                          onClick={() => openCourseViewer(course.id)}
                        >
                          {/* Expand button */}
                          {hasContent ? (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleCourseExpand(course.id);
                              }}
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </Button>
                          ) : (
                            <div className="w-8" />
                          )}

                          {/* Course icon */}
                          <div className={cn(
                            "flex h-10 w-10 items-center justify-center rounded-lg shrink-0",
                            isCourseHidden ? "bg-yellow-100" : "bg-primary/10"
                          )}>
                            {hasContent && isExpanded ? (
                              <FolderOpen className={cn("h-5 w-5", isCourseHidden ? "text-yellow-600" : "text-primary")} />
                            ) : (
                              <Folder className={cn("h-5 w-5", isCourseHidden ? "text-yellow-600" : "text-primary")} />
                            )}
                          </div>

                          {/* Course info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold truncate">{course.title}</p>
                              {isCourseHidden && (
                                <Badge variant="outline" className="text-yellow-600 border-yellow-300 bg-yellow-100 shrink-0">
                                  Caché
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-1">
                              {course.description || "Pas de description"}
                            </p>
                            <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                              {hasSections && (
                                <span className="flex items-center gap-1">
                                  <Layers className="h-3 w-3" />
                                  {course.sections.length} section(s)
                                </span>
                              )}
                              {courseDirectItems > 0 && (
                                <span>
                                  {courseItems.tps.length > 0 && `${courseItems.tps.length} TP`}
                                  {courseItems.quizzes.length > 0 && `, ${courseItems.quizzes.length} Quiz`}
                                  {courseItems.assignments.length > 0 && `, ${courseItems.assignments.length} Devoir`}
                                  {(courseItems.url_resources?.length || 0) > 0 && `, ${courseItems.url_resources.length} URL`}
                                </span>
                              )}
                              {isAdmin && course.created_by_name && (
                                <span className="flex items-center gap-1 text-violet-500">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                  Créé par {course.created_by_name}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => openCourseViewer(course.id)}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Voir le cours</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            {canManage && (
                              <>
                                {/* Reorder controls */}
                                {reorderMode && (
                                  <div className="flex items-center gap-0.5 mr-1 border-r pr-2">
                                    <GripVertical className="h-4 w-4 text-muted-foreground/50" />
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            disabled={reordering || group.courses.indexOf(course) === 0}
                                            onClick={(e) => { e.stopPropagation(); handleMoveUp(course, group.courses); }}
                                          >
                                            <ChevronUp className="h-4 w-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Monter</TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            disabled={reordering || group.courses.indexOf(course) === group.courses.length - 1}
                                            onClick={(e) => { e.stopPropagation(); handleMoveDown(course, group.courses); }}
                                          >
                                            <ChevronDown className="h-4 w-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Descendre</TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </div>
                                )}
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleToggleVisibility('course', course.id)}
                                      >
                                        {isCourseHidden ? (
                                          <EyeOff className="h-4 w-4 text-yellow-600" />
                                        ) : (
                                          <Eye className="h-4 w-4 text-green-600" />
                                        )}
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {isCourseHidden ? "Rendre visible" : "Cacher tout"}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => navigate(`/workspace/${workspaceId}/courses/${course.id}`)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    setDeleteId(course.id);
                                    setDeleteType('course');
                                  }}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                                <ShareButton
                                  contentType="course"
                                  contentId={course.id}
                                  workspaceId={workspaceId}
                                  title={course.title}
                                  variant="ghost"
                                  size="icon"
                                />
                                {/* More actions dropdown */}
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" data-testid={`course-actions-${course.id}`}>
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-56 z-[100]" sideOffset={5}>
                                    <DropdownMenuItem onClick={() => openDuplicateMoveDialog('duplicate', 'course', course.id, course.title)}>
                                      <Copy className="h-4 w-4 mr-2" />
                                      Dupliquer
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openDuplicateMoveDialog('move', 'course', course.id, course.title)}>
                                      <FolderOutput className="h-4 w-4 mr-2" />
                                      Déplacer
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <div className="px-3 py-2" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                                      <p className="text-xs text-muted-foreground mb-2.5 flex items-center gap-1.5 font-medium"><Palette className="h-3.5 w-3.5" /> Couleur</p>
                                      <div className="flex flex-wrap gap-2">
                                        {[
                                          null, '#ef4444', '#f97316', '#eab308', '#22c55e', 
                                          '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6', '#ec4899'
                                        ].map((c) => (
                                          <button
                                            key={c || 'none'}
                                            className={cn(
                                              "w-7 h-7 rounded-full border-2 transition-all hover:scale-125 focus:outline-none",
                                              c === (course.color || null) ? 'border-foreground ring-2 ring-offset-1 ring-primary scale-110' : 'border-muted hover:border-foreground/50'
                                            )}
                                            style={{ 
                                              background: c || 'linear-gradient(135deg, #e5e7eb 45%, transparent 45%, transparent 55%, #e5e7eb 55%)',
                                              ...(c ? {} : { border: '2px solid #d1d5db' })
                                            }}
                                            title={c ? c : 'Aucune couleur'}
                                            onPointerDown={(e) => e.stopPropagation()}
                                            onClick={async (e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              try {
                                                await courseAPI.updateColor(workspaceId, course.id, c);
                                                loadTree();
                                                toast.success('Couleur mise à jour');
                                              } catch (err) {
                                                toast.error('Erreur lors de la mise à jour de la couleur');
                                              }
                                            }}
                                          />
                                        ))}
                                      </div>
                                    </div>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Course content (sections + direct items + linked sites) */}
                        {hasContent && isExpanded && (
                          <div className="border-t bg-muted/20">
                            {/* Direct course items (not in a section) */}
                            {courseDirectItems > 0 && (
                              <div className="py-2">
                                {courseItems.tps.map(tp => renderItem(tp, 'tp', isCourseHidden, 2))}
                                {courseItems.quizzes.map(quiz => renderItem(quiz, 'quiz', isCourseHidden, 2))}
                                {courseItems.assignments.map(assignment => renderItem(assignment, 'assignment', isCourseHidden, 2))}
                                {(courseItems.url_resources || []).map(ur => renderItem(ur, 'url_resource', isCourseHidden, 2))}
                              </div>
                            )}

                            {/* Sections */}
                            {hasSections && (
                              <div className="py-2">
                                {course.sections
                                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                                  .map((section, index) => renderSection(course, section, index, isCourseHidden))}
                              </div>
                            )}

                            {/* Linked Static Sites */}
                            {hasLinkedSites && (
                              <div className={cn(
                                "py-2",
                                (hasSections || courseDirectItems > 0) && "border-t border-dashed"
                              )}>
                                <div className="px-4 py-2 text-sm font-medium text-muted-foreground flex items-center gap-2">
                                  <Globe className="h-4 w-4 text-purple-500" />
                                  Sites statiques liés ({linkedSites[course.id].length})
                                </div>
                                {linkedSites[course.id].map((site) => {
                                  const isSiteHidden = site.is_visible === false;
                                  return (
                                  <div
                                    key={site.id}
                                    className={cn(
                                      "flex items-center gap-3 py-2 px-4 hover:bg-muted/50 transition-colors ml-4",
                                      isSiteHidden && "opacity-50"
                                    )}
                                  >
                                    <Globe className="h-4 w-4 text-purple-500 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <p className="font-medium truncate">{site.name}</p>
                                        {isSiteHidden && (
                                          <Badge variant="outline" className="text-yellow-600 border-yellow-300 bg-yellow-100 shrink-0 text-[10px] px-1.5 py-0">
                                            Caché
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="text-xs text-muted-foreground">
                                        {site.pages?.length || 0} pages • {site.source === 'git' ? `Git (${site.provider})` : 'ZIP'}
                                      </p>
                                    </div>
                                    {!isSiteHidden && (
                                      <Badge variant="outline" className="text-green-600 border-green-200 shrink-0">
                                        Live
                                      </Badge>
                                    )}
                                    {/* Visibility toggle */}
                                    {canManage && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="h-7 w-7 shrink-0"
                                              onClick={async (e) => {
                                                e.stopPropagation();
                                                try {
                                                  const token = localStorage.getItem('token');
                                                  const response = await fetch(
                                                    `${process.env.REACT_APP_BACKEND_URL}/api/sites/${site.id}/visibility`,
                                                    {
                                                      method: 'PATCH',
                                                      headers: { 'Authorization': `Bearer ${token}` }
                                                    }
                                                  );
                                                  if (response.ok) {
                                                    toast.success(isSiteHidden ? 'Site affiché' : 'Site masqué');
                                                    loadTree();
                                                  }
                                                } catch (error) {
                                                  toast.error('Erreur lors du changement de visibilité');
                                                }
                                              }}
                                            >
                                              {isSiteHidden ? (
                                                <EyeOff className="h-3.5 w-3.5 text-yellow-600" />
                                              ) : (
                                                <Eye className="h-3.5 w-3.5 text-green-600" />
                                              )}
                                            </Button>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            {isSiteHidden ? 'Rendre visible' : 'Masquer le site'}
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                    {/* Share button */}
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 shrink-0"
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              try {
                                                const token = localStorage.getItem('token');
                                                const backendUrl = encodeURIComponent(process.env.REACT_APP_BACKEND_URL);
                                                const response = await fetch(
                                                  `${process.env.REACT_APP_BACKEND_URL}/api/share/create?content_type=site&content_id=${site.id}&workspace_id=${workspaceId}&title=${encodeURIComponent(site.name)}&backend_url=${backendUrl}`,
                                                  {
                                                    method: 'POST',
                                                    headers: { 'Authorization': `Bearer ${token}` }
                                                  }
                                                );
                                                if (response.ok) {
                                                  const data = await response.json();
                                                  const shareUrl = data.share_url || `${window.location.origin}/share/${data.token}`;
                                                  navigator.clipboard.writeText(shareUrl);
                                                  toast.success('Lien de partage copié !');
                                                } else {
                                                  throw new Error('Failed to create share link');
                                                }
                                              } catch (error) {
                                                const siteUrl = `${process.env.REACT_APP_BACKEND_URL}/s/${site.slug || site.id}/`;
                                                navigator.clipboard.writeText(siteUrl);
                                                toast.success('Lien direct copié !');
                                              }
                                            }}
                                          >
                                            <Share2 className="h-3.5 w-3.5" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Partager</TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                    {/* Open in new tab */}
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 shrink-0"
                                            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/s/${site.slug || site.id}/`, '_blank')}
                                          >
                                            <ExternalLink className="h-3.5 w-3.5" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Ouvrir le site</TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              );
            })}

            {/* Unassigned items */}
            {activeTab === 'unassigned' && hasUnassignedItems && (
                <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <div className="border rounded-lg bg-muted/20 py-2">
                    {treeData.unassigned.tps.map(tp => renderItem(tp, 'tp', false, 1))}
                    {treeData.unassigned.quizzes.map(quiz => renderItem(quiz, 'quiz', false, 1))}
                    {treeData.unassigned.assignments.map(assignment => renderItem(assignment, 'assignment', false, 1))}
                    {(treeData.unassigned.url_resources || []).map(ur => renderItem(ur, 'url_resource', false, 1))}
                  </div>
                </div>
            )}
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => { setDeleteId(null); setDeleteType(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer cet élément ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} disabled={deleting}>
                {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Course Viewer Modal */}
        <Dialog open={viewerOpen} onOpenChange={closeCourseViewer}>
          <DialogContent 
            ref={viewerRef}
            className={cn(
              "max-w-6xl h-[85vh] p-0 flex flex-col overflow-hidden",
              isFullscreen && "max-w-none w-screen h-screen rounded-none"
            )}
          >
            {courseLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : viewingCourse ? (
              <>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b bg-muted/30">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-semibold truncate">{viewingCourse.title}</h2>
                    {viewingCourse.description && (
                      <p className="text-sm text-muted-foreground truncate">{viewingCourse.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={toggleFullscreen}
                      className="gap-2"
                    >
                      {isFullscreen ? (
                        <>
                          <Minimize2 className="h-4 w-4" />
                          Quitter plein écran
                        </>
                      ) : (
                        <>
                          <Maximize2 className="h-4 w-4" />
                          Plein écran
                        </>
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={closeCourseViewer}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 flex overflow-hidden">
                  {/* Styles for HTML blocks */}
                  <style>{`
                    .course-content [data-google-slides] iframe {
                      min-height: 500px !important;
                    }
                    .course-content [data-drive-video] iframe {
                      min-height: 400px !important;
                    }
                    .course-content [data-html-block] iframe,
                    .course-content div.my-4 > iframe[srcdoc] {
                      width: 100% !important;
                      border: none !important;
                    }
                    ${!isAdmin ? `
                    .course-content [data-google-slides] .aspect-video::after {
                      content: '';
                      position: absolute;
                      bottom: 0;
                      right: 0;
                      width: 140px;
                      height: 38px;
                      background-color: #f1f3f4;
                      pointer-events: all;
                      z-index: 10;
                    }
                    .dark .course-content [data-google-slides] .aspect-video::after {
                      background-color: #2b2b2b; /* In case the slide bar turns dark in dark mode */
                    }
                    ` : ''}
                  `}</style>
                  {/* Main content */}
                  <div className="flex-1 overflow-y-auto p-6">
                    {/* Introduction/Content */}
                    {viewingCourse.content && (
                      <div className="mb-8">
                        <h3 className="text-lg font-semibold mb-4">Introduction</h3>
                        <HtmlContentWithPdfSlides 
                          html={cleanHtmlContent(viewingCourse.content)}
                          className="course-content prose prose-sm max-w-none dark:prose-invert"
                        />
                      </div>
                    )}

                    {/* Active Section Content */}
                    {activeSection && (
                      <div>
                        <h3 className="text-lg font-semibold mb-4">{activeSection.title}</h3>
                        {activeSection.description && (
                          <p className="text-muted-foreground mb-4">{activeSection.description}</p>
                        )}
                        {activeSection.content && (
                          <HtmlContentWithPdfSlides 
                            html={cleanHtmlContent(activeSection.content)}
                            className="course-content prose prose-sm max-w-none dark:prose-invert"
                          />
                        )}
                      </div>
                    )}

                    {/* No content message */}
                    {!viewingCourse.content && !activeSection && (
                      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                        <BookOpen className="h-12 w-12 mb-4 opacity-50" />
                        <p>Ce cours n'a pas encore de contenu.</p>
                      </div>
                    )}
                  </div>

                  {/* Sidebar with sections */}
                  {viewingCourse.sections?.length > 0 && (
                    <div className="w-72 border-l bg-muted/20 overflow-y-auto">
                      <div className="p-4">
                        <h4 className="font-semibold mb-3 text-sm uppercase text-muted-foreground">Sections</h4>
                        <div className="space-y-1">
                          {viewingCourse.sections.map((section, index) => (
                            <button
                              key={section.id}
                              onClick={() => setActiveSection(section)}
                              className={cn(
                                "w-full text-left px-3 py-2 rounded-md text-sm transition-colors",
                                activeSection?.id === section.id
                                  ? "bg-primary text-primary-foreground"
                                  : "hover:bg-muted"
                              )}
                            >
                              <span className="font-medium">{index + 1}. {section.title}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Category Dialog */}
        <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingCategory ? "Modifier la catégorie" : "Nouvelle catégorie"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Nom *</label>
                <input
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder="Ex: DevOps"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  data-testid="category-name-input"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Description</label>
                <input
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder="Description optionnelle"
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                />
              </div>
              <div className="flex gap-4">
                <div className="space-y-2 flex-1">
                  <label className="text-sm font-medium">Couleur</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={categoryForm.color}
                      onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                      className="h-10 w-14 rounded border cursor-pointer"
                    />
                    <span className="text-sm text-muted-foreground">{categoryForm.color}</span>
                  </div>
                </div>
                <div className="space-y-2 flex-1">
                  <label className="text-sm font-medium">Ordre</label>
                  <input
                    type="number"
                    min="0"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={categoryForm.order}
                    onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
              {/* Existing categories list */}
              {categories.length > 0 && !editingCategory && (
                <div className="border-t pt-4">
                  <label className="text-sm font-medium text-muted-foreground">Catégories existantes</label>
                  <div className="mt-2 space-y-1">
                    {categories.map(cat => (
                      <div key={cat.id} className="flex items-center gap-2 p-2 rounded hover:bg-muted/50">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: cat.color }} />
                        <span className="text-sm flex-1">{cat.name}</span>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setCategoryDialogOpen(false); setTimeout(() => openCategoryDialog(cat), 100); }}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleDeleteCategory(cat.id)}>
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setCategoryDialogOpen(false)}>Annuler</Button>
                <Button className="flex-1" onClick={handleSaveCategory} disabled={savingCategory || !categoryForm.name.trim()}>
                  {savingCategory ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editingCategory ? "Modifier" : "Créer"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Duplicate/Move Dialog */}
        <DuplicateMoveDialog
          open={duplicateMoveOpen}
          onOpenChange={setDuplicateMoveOpen}
          mode={duplicateMoveMode}
          contentType={duplicateMoveType}
          contentId={duplicateMoveId}
          contentTitle={duplicateMoveTitle}
          currentWorkspaceId={workspaceId}
          onSuccess={() => {
            loadTree();
          }}
        />
      </div>
    </WorkspaceLayout>
  );
}
